import React, { Fragment, useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { addDays } from "date-fns/esm";
import { defineMessages, IntlShape, useIntl } from "react-intl";

import { CardCollapse, CollapsePanel } from "@mapmycustomers/ui";

import ButtonLink from "@app/component/ButtonLink";
import { isLeadFinderBasic } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { getWeekStart } from "@app/util/dates";
import { formatDate } from "@app/util/formatters";
import { parseTime24hr } from "@app/util/parsers/parseTime";

import styles from "./LeadItemHours.module.scss";

const messages = defineMessages({
  basicPlan: {
    id: "map.tool.leadFinder.hours.basicPlan",
    defaultMessage: "Get business hours by upgrading <a>Lead Finder</a>",
    description: "Lead Finder hours - Basic Plan notice",
  },
  closed: {
    id: "map.tool.leadFinder.hours.closed",
    defaultMessage: "Closed",
    description: "Lead Finder hours - Closed",
  },
  closedToday: {
    id: "map.tool.leadFinder.hours.closedToday",
    defaultMessage: "Closed Today",
    description: "Lead Finder hours - Closed Today",
  },
  open: {
    id: "map.tool.leadFinder.hours.open",
    defaultMessage: "Open {hours}",
    description: "Lead Finder hours - open hours",
  },
  open24hr: {
    id: "map.tool.leadFinder.hours.open24hr",
    defaultMessage: "Open 24 hours",
    description: "Lead Finder hours - open 24 hours",
  },
  openRange: {
    id: "map.tool.leadFinder.hours.openRange",
    defaultMessage: "{openTime} - {closeTime}",
    description: "Lead Finder hours - open hours range",
  },
});

interface Props {
  basicPlan: boolean;
  openingHours?: Omit<google.maps.places.PlaceOpeningHours, "isOpen">;
}

const formatHours = (
  intl: IntlShape,
  periods?: google.maps.places.PlaceOpeningHoursPeriod[],
  currentDayOfWeek?: number // undefined for "today"
): string | undefined => {
  const currentDay = currentDayOfWeek ?? new Date().getDay();

  if (!periods?.length) {
    return;
  }

  const currentDayHours = periods.filter((period) => {
    if (period && period.open?.day !== undefined) {
      const open = period.open?.day;
      const close = period.close?.day;

      // basically, we assume two things here:
      // 1) google sends hours for *each day separately*. I.e. if place is open 08-20 mo-fr,
      // then we will get a schedule 5 times - for each day, instead of a single
      // item like {open: {day: 1, time: "0800"}, close: {day: 5, time: "2000"}}
      // 2) close day can be bigger when place closes the next day. E.g. opening
      // hours are like 8pm - 2am
      if (close !== undefined && open < close) {
        return currentDay >= open && currentDay < close;
      } else {
        return open === currentDay;
      }
    }
    return false;
  });

  if (currentDayHours.length) {
    const isOpen24hr =
      currentDayHours.length === 1 &&
      currentDayHours[0]?.open?.time === "0000" &&
      (currentDayHours[0]?.close?.time === "0000" || !currentDayHours[0]?.close?.time);
    if (isOpen24hr) {
      return intl.formatMessage(messages.open24hr);
    }
    const hours = currentDayHours
      .map((range) => {
        const openTime = formatDate(parseTime24hr(range.open?.time), "p");
        const closeTime = formatDate(parseTime24hr(range.close?.time), "p");
        return intl.formatMessage(messages.openRange, { closeTime, openTime });
      })
      .join(", ");
    return intl.formatMessage(messages.open, { hours });
  } else {
    if (periods.length === 1 && periods[0]?.open?.time === "0000") {
      return intl.formatMessage(messages.open24hr);
    }
  }

  return intl.formatMessage(
    currentDayOfWeek !== undefined ? messages.closed : messages.closedToday
  );
};

const LeadItemHours: React.FC<Props> = ({ basicPlan, openingHours }) => {
  const analytics = useAnalytics();
  const intl = useIntl();

  const periods = useMemo(
    () => Array.from({ length: 7 }, (_, day) => formatHours(intl, openingHours?.periods, day)),
    [openingHours, intl]
  );

  const days = useMemo(() => {
    const weekStart = getWeekStart(new Date());
    return Array.from({ length: 7 }, (_, day) => formatDate(addDays(weekStart, day), "cccc"));
  }, []);

  const todayHours = formatHours(intl, openingHours?.periods);
  const time = (
    <>
      {todayHours}
      {openingHours?.open_now && <span className={styles.open} />}
    </>
  );

  const handleChange = useCallback(
    (keys) => {
      analytics.clicked(["Open hours"], { opening: keys.length > 0 });
    },
    [analytics]
  );

  const handleBasicHoursClick = useCallback(() => {
    analytics.clicked(["Get business hours by upgrading"]);
    window.open("https://share.hsforms.com/195I7JIs9Qg69KxGekOA8wA3is1h", "_blank");
  }, [analytics]);

  return basicPlan ? (
    <div className={styles.basicPlan}>
      {intl.formatMessage(messages.basicPlan, {
        a: (text) => <ButtonLink onClick={handleBasicHoursClick}>{text}</ButtonLink>,
      })}
    </div>
  ) : todayHours ? (
    <CardCollapse className={styles.container} onChange={handleChange} size="small">
      <CollapsePanel header={time} key="hours">
        <div className={styles.time}>
          {periods.map((hours, index) => (
            <Fragment key={`day_${index}`}>
              <span className={styles.day}>{days[index]}</span>
              <span>{hours}</span>
            </Fragment>
          ))}
        </div>
      </CollapsePanel>
    </CardCollapse>
  ) : null;
};

const mapStateToProps = (state: RootState) => ({
  basicPlan: isLeadFinderBasic(state),
});

export default connect(mapStateToProps)(LeadItemHours);
